import { useState } from "react";
import IdentifyForm from "./forms/IdentifyForm";
import VerifyCodeForm from "./forms/VerifyCodeForm";
import PasswordLoginForm from "./forms/PasswordLoginForm";
import ForgotPasswordForm from "./forms/ForgotPasswordForm";
import { saveOtcRequestTimestamp } from "@/lib/storage-utils";

export type AuthView =
  | "identify"
  | "verify-code"
  | "password-login"
  | "forgot-password"
  // Legacy values — both map to "identify"
  | "login"
  | "signup";

type AuthFlowProps = {
  initialView?: AuthView;
  initialEmail?: string;
  initialAuthMethods?: string[];
  initialPhase?: "signup" | "password";
  onSuccess: () => void;
  compact?: boolean;
  hideHeading?: boolean;
  codeFirst?: boolean;
};

function resolveInitialView(
  view: AuthView | undefined,
): "identify" | "verify-code" | "password-login" | "forgot-password" {
  if (!view || view === "login" || view === "signup") return "identify";
  return view;
}

export function AuthFlow({
  initialView,
  initialEmail,
  initialAuthMethods,
  initialPhase,
  onSuccess,
  compact,
  hideHeading,
  codeFirst,
}: Readonly<AuthFlowProps>) {
  const resolvedView = resolveInitialView(initialView);
  const safeView =
    resolvedView === "verify-code" && !initialEmail ? "identify" : resolvedView;

  const [view, setView] = useState(safeView);
  const [email, setEmail] = useState(initialEmail ?? "");
  const [authMethods, setAuthMethods] = useState<string[]>(
    initialAuthMethods ?? [],
  );

  switch (view) {
    case "identify":
      return (
        <IdentifyForm
          onCodePath={(submittedEmail, methods) => {
            setEmail(submittedEmail);
            if (methods) setAuthMethods(methods);
            saveOtcRequestTimestamp(submittedEmail);
            setView("verify-code");
          }}
          onSuccess={onSuccess}
          onForgotPassword={(submittedEmail) => {
            setEmail(submittedEmail);
            setView("forgot-password");
          }}
          initialEmail={email}
          initialPhase={initialPhase}
          compact={compact}
          hideHeading={hideHeading}
          codeFirst={codeFirst}
        />
      );
    case "verify-code":
      return (
        <VerifyCodeForm
          email={email}
          onSuccess={onSuccess}
          onPasswordFallback={() => setView("password-login")}
          onBack={() => {
            setEmail("");
            setAuthMethods([]);
            setView("identify");
          }}
          compact={compact}
          authMethods={authMethods}
        />
      );
    case "password-login":
      return (
        <PasswordLoginForm
          email={email}
          onSuccess={onSuccess}
          onForgotPassword={() => setView("forgot-password")}
          onCodeInstead={() => setView("verify-code")}
          onUseDifferentEmail={() => {
            setEmail("");
            setView("identify");
          }}
          compact={compact}
        />
      );
    case "forgot-password":
      return (
        <ForgotPasswordForm
          onSuccess={() => setView("identify")}
          onBackToLogin={() => setView("identify")}
          compact={compact}
        />
      );
  }
}

export default AuthFlow;
